import { useState } from "react";
import { Link } from "react-router-dom";
import { Section, Pill } from "./PortalPage";
import { fmtDate, JOB_STATUS } from "./portalUtils";
import { SubJobProgressBar } from "../jobs/SubJobProgress";
import { Button } from "../ui/button";
import { downloadAuthFile } from "../../lib/fileOpen";
import { TreeStructure, CaretRight, CheckCircle, FilePdf, Images } from "@phosphor-icons/react";

/* Client: download the Work Completed Report (a main job's rolls up every finished item's before/after photos) */
export function ClientPhotoReportButton({ job, compact = false }) {
  const [busy, setBusy] = useState(false);
  const isParent = !!job.sub_job_summary?.total;
  const done = isParent ? job.sub_job_summary.completed || 0 : job.status === "completed" ? 1 : 0;
  if (!done) return null;
  const partial = isParent && done < job.sub_job_summary.total;
  const download = async () => {
    setBusy(true);
    await downloadAuthFile(`/api/client-hub/jobs/${job.id}/completion-report`, `${job.reference}_completion_report.pdf`);
    setBusy(false);
  };
  return (
    <Button size="sm" variant={compact ? "ghost" : "outline"} className={compact ? "h-7 text-xs" : "h-8 text-xs"} onClick={download} disabled={busy} data-testid="client-photo-report-btn"
      title={isParent ? "One PDF with every completed item's before and after photos" : "Before and after photos for this job"}>
      {isParent ? <Images size={13} className="mr-1.5" /> : <FilePdf size={13} className="mr-1.5" />}
      {busy ? "Preparing…" : isParent ? `Photo report${partial ? ` (${done} of ${job.sub_job_summary.total} items)` : ""}` : "Completion report"}
    </Button>
  );
}

/* Client-portal view of a main job's sub-jobs */
export function ClientSubJobs({ job, teamLabel }) {
  const kids = job.sub_jobs || [];
  if (!kids.length) return null;
  return (
    <Section title={<span className="flex items-center gap-1.5"><TreeStructure size={14} /> Items of work ({job.sub_job_summary?.completed || 0} of {kids.length} complete)</span>} action={<ClientPhotoReportButton job={job} compact />} testid="client-sub-jobs">
      <SubJobProgressBar summary={job.sub_job_summary} className="mb-3" testid="client-sub-jobs-progress" />
      <div className="divide-y divide-border border border-border rounded-md">
        {kids.map((k) => (
          <Link key={k.id} to={`/portal/jobs/${k.id}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors" data-testid={`client-sub-job-${k.id}`}>
            <span className="h-6 w-6 shrink-0 rounded-full bg-muted text-[10px] font-bold flex items-center justify-center text-muted-foreground">{k.sub_job_index || "•"}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate flex items-center gap-1.5">{k.title}{k.client_sign_off && <CheckCircle size={12} weight="fill" className="text-emerald-600" />}</p>
              <p className="text-[10px] text-muted-foreground">
                <span className="font-mono">{k.reference}</span>
                {" · "}{k.scheduled_start ? fmtDate(k.scheduled_start) : "Date TBC"}
                {k.contractor ? ` · ${k.contractor}` : k.contractor_assigned ? ` · ${teamLabel?.assigned || "Assigned"}` : ""}
              </p>
            </div>
            <Pill map={JOB_STATUS} value={k.status} />
            <CaretRight size={12} className="text-muted-foreground shrink-0" />
          </Link>
        ))}
      </div>
      {(job.sub_job_summary?.completed || 0) > 0 && (
        <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1.5" data-testid="client-photo-report-hint">
          <Images size={12} /> The photo report bundles every finished item's before and after photos into one PDF{(job.sub_job_summary.completed < kids.length) ? " — it grows as more items are completed." : "."}
        </p>
      )}
    </Section>
  );
}

/* Link back to the main job, shown on a sub-job's portal page */
export function ClientParentJobLink({ job }) {
  const p = job.parent_job;
  if (!job.parent_job_id) return null;
  return (
    <Link to={`/portal/jobs/${job.parent_job_id}`} className="text-[11px] text-primary hover:underline flex items-center gap-1" data-testid="client-parent-job-link">
      <TreeStructure size={11} /> Item {job.sub_job_index || ""}{p?.sub_job_summary?.total ? ` of ${p.sub_job_summary.total}` : ""} in {p?.reference || job.parent_job_reference}
    </Link>
  );
}
